/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as GuildActionCreators from '@app/actions/GuildActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import styles from '@app/components/invites/DisableInvitesButton.module.css';
import {ConfirmModal} from '@app/components/modals/ConfirmModal';
import {Button} from '@app/components/uikit/button/Button';
import GuildStore from '@app/stores/GuildStore';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';

export const DisableInvitesButton = observer(({guildId}: {guildId: string}) => {
	const {t} = useLingui();
	const guild = GuildStore.getGuild(guildId);
	const invitesDisabled = guild?.features.has('INVITES_DISABLED') ?? false;
	const isRaidDetected = guild?.features.has(GuildFeatures.RAID_DETECTED) ?? false;

	const handleToggleInvites = useCallback(() => {
		ModalActionCreators.push(
			modal(() => (
				<ConfirmModal
					title={invitesDisabled ? t`Enable invites for this community` : t`Disable invites for this community`}
					description={
						invitesDisabled ? (
							<Trans>
								Are you sure you want to enable invites? This will allow users to join this community through invite
								links again.
							</Trans>
						) : (
							<Trans>
								Are you sure you want to disable invites? This will prevent new users from joining through invite links
								until you re-enable them. Existing members will not be affected.
							</Trans>
						)
					}
					primaryText={invitesDisabled ? t`Enable` : t`Disable`}
					primaryVariant={invitesDisabled ? 'primary' : 'danger-primary'}
					secondaryText={t`Cancel`}
					onPrimary={async () => {
						await GuildActionCreators.toggleInvitesDisabled(guildId, !invitesDisabled);
					}}
				/>
			)),
		);
	}, [guildId, invitesDisabled]);

	return (
		<div className={styles.container}>
			<Button variant={invitesDisabled ? 'danger-primary' : 'secondary'} small={true} onClick={handleToggleInvites}>
				{invitesDisabled ? <Trans>Enable Invites</Trans> : <Trans>Pause Invites</Trans>}
			</Button>
			{invitesDisabled && (
				<p className={styles.message}>
					{isRaidDetected ? (
						<Trans>
							Invites are currently disabled because Fluxer detected a potential raid. New users can't join right now.
						</Trans>
					) : (
						<Trans>Invites are currently disabled for this community.</Trans>
					)}
				</p>
			)}
		</div>
	);
});
